import { UploadOutlined } from '@ant-design/icons'
import { Button, Upload } from 'antd'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { saveAs } from 'file-saver'
import { parse as parseCSV, unparse } from 'papaparse'
import { useTranslation } from 'react-i18next'
import type { z } from 'zod'

// enables parsing dates from explicit format strings, e.g. dayjs(input, 'DD.MM.YYYY')
dayjs.extend(customParseFormat)

/**
 * Generates a CSV file and triggers a browser download
 */
const triggerCSVBrowserDownload = (
  csvRows: any[],
  filename = 'invalid_data.csv'
) => {
  const CSVFile = new Blob([unparse(csvRows)], {
    type: 'text/csv;charset=utf-8',
  })
  saveAs(CSVFile, filename)
}

/**
 * Converts CSV date in `DD.MM.YYYY` format into backend accepted format.
 *
 * Returns an empty string for missing or invalid input so that schema
 * validation routes the row into the invalid bucket. Without the guard,
 * dayjs(undefined) would resolve to "now" and silently upload today's date.
 */
export const parseCSVDate = (CSVInputDate: string) => {
  if (!CSVInputDate) {
    return ''
  }
  const parsedDate = dayjs(CSVInputDate, 'DD.MM.YYYY', true)
  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : ''
}

/**
 * COMPONENT
 */

interface Props {
  schema: z.ZodType<any>
  onParsed: (
    columns: object[]
  ) => Promise<void | { error: any; sourceId: string }>[]
  onFailure: (error: Error) => void
  buttonLabel: string
  parseDataFromRow: (row: any) => { id: string }
}

const SourcesBulkCSVImporter = ({
  onParsed,
  onFailure,
  buttonLabel,
  schema,
  parseDataFromRow,
}: Props) => {
  const { t } = useTranslation()

  return (
    <Upload
      showUploadList={false}
      beforeUpload={fileContent => {
        parseCSV(fileContent, {
          header: true,
          delimiter: ',',
          complete: async parseResults => {
            const rows = parseResults.data
            const columns = rows.reduce<{
              parsedRaw: { [k: string]: any }
              parsed: any[]
              invalid: any[]
            }>(
              (result, row) => {
                const parsedData = parseDataFromRow(row)

                return schema.safeParse(parsedData).success
                  ? // When valid, we store the parsed data into `parsed`
                    {
                      parsedRaw: { ...result.parsedRaw, [parsedData.id]: row },
                      parsed: [...result.parsed, parsedData],
                      invalid: result.invalid,
                    }
                  : // When invalid, we store the raw row data into `invalid`
                    {
                      parsedRaw: result.parsedRaw,
                      parsed: result.parsed,
                      invalid: [...result.invalid, row],
                    }
              },

              {
                parsedRaw: {},
                parsed: [],
                invalid: [],
              }
            )

            // if there are invalid rows in the uploaded CSV, the process stops
            // and we provide a file containing the rows that were not accepted
            if (columns.invalid.length > 0) {
              triggerCSVBrowserDownload(columns.invalid)
              onFailure(new Error(t('sources.invalidCSVFile')))
            }

            // When there are no invalid rows in the uploaded CSV, we trigger onParse.
            // Then, we check for failures (ie: the source update mutation failed)
            // If there are failure cases, we generate a CSV file containing the
            // input data for all sources that failed to update
            else {
              // extract failures from resolved promises
              const mutationFailures: string[] = (
                await Promise.all(onParsed(columns.parsed))
              ).reduce(
                (failureCases, result) =>
                  result && (result as any).error
                    ? [...failureCases, (result as any).sourceId]
                    : failureCases,
                [] as string[]
              )

              // trigger CSV file download containing the data for each source
              // that failed to update
              triggerCSVBrowserDownload(
                mutationFailures
                  .map(sourceId => columns.parsedRaw[sourceId])
                  .filter(data => !!data)
              )
            }
          },
        })
        return false // prevent automatic upload
      }}
      accept="text/csv"
    >
      <Button type="primary" icon={<UploadOutlined />}>
        {buttonLabel}
      </Button>
    </Upload>
  )
}

export default SourcesBulkCSVImporter
